import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { ReactComponent as SvgSearchClose } from '@img/icons/cross-icon.svg';
import { ReactComponent as SvgSearch } from '@img/icons/search-icon.svg';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.div `
  ${setCssVar('accentColor', brand.sage.sageMedium)};
  ${setCssVar('bgColor', brand.white)};
  ${setCssVar('bgColorAlt', brand.sage.sageLight)};
  ${setCssVar('fgColor', brand.sage.sageLight)};
  ${setCssVar('fgColorAlt', brand.white)};

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  padding: 10px 15px;
  background-color: ${brand.sage.sageDark};
  z-index: 101;
  transform: translateX(-100%);
  transition: transform 0.3s ${animation.sharp};
  visibility: hidden;

  @media (prefers-reduced-motion: reduce)  {
    transition: none;
  }

  @media ${from(Device.Tablet)} {
    padding: 15px 60px;
  }

  @media ${from(Device.DesktopSmall)} {
    padding: 60px 80px;
  }

  ${({ active }) => active &&
    css `
      transform: translateX(0);
      visibility: visible;
    `}
`;
const Inner = styled.div `
  position: relative;
  display: block;
  padding-bottom: 10px;
  border-bottom: 1px solid ${brand.sage.sageMedium};

  @media ${from(Device.Tablet)} {
    padding-bottom: 15px;
  }

  @media ${from(Device.DesktopSmall)} {
    padding-bottom: 50px;
  }
`;
const SearchContainer = styled.div `
  position: relative;
  max-width: 85%;
  margin: 0;
  display: flex;

  @media ${from(Device.Tablet)} {
    max-width: 441px;
    margin: auto;
  }

  @media ${from(Device.DesktopSmall)} {
    max-width: 860px;
  }
`;
const Input = styled.input `
  display: block;
  appearance: none !important; // important to override reset style
  background: ${cssVar('bgColor')};
  border-radius: 5px;
  border: none;
  color: ${cssVar('fgColor')};
  flex: 1 0 auto;
  ${fonts.matter.regular};
  font-size: 16px;
  letter-spacing: 0.1em;
  line-height: 24px;
  max-width: 860px;
  outline: none;
  padding: 11px 50px 11px 10px;
  text-transform: uppercase;

  @media ${from(Device.Tablet)} {
    padding: 12px 60px 12px 10px;
  }

  @media ${from(Device.Desktop)} {
    min-width: 400px;
  }

  &:focus {
    outline: none;
  }

  &.focus-visible {
    border-color: ${cssVar('fgColor')};
  }
`;
const Submit = styled.button `
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-content: center;
  align-items: center;
  background-color: ${cssVar('bgColorAlt')};
  border: none;
  color: ${cssVar('fgColorAlt')};
  flex: 0 0 45px;
  height: 46px;
  padding: 5px;
  justify-content: center;
  justify-items: center;
  width: 46px;
  border-radius: 0 3px 3px 0;

  @media ${from(Device.Tablet)} {
    height: 48px;
    width: 57px;
    padding: 10px;
  }

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    background-color: ${cssVar('accentColor')};
  }
`;
const SubmitLabel = styled.span `
  ${srOnly}
`;
const SubmitIcon = styled(SvgSearch) `
  display: block;
  color: currentColor;
  height: 33px;
  width: 33px;
  transition: background-color 0.3s;
`;
const Close = styled.button `
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-content: center;
  align-items: center;
  background: none;
  border: none;
  color: ${cssVar('fgColor')};
  flex: 0 0 45px;
  height: 50px;
  margin: 0;
  padding: 0;
  justify-content: center;
  justify-items: center;
  width: 45px;

  svg {
    transition: transform 1s ${animation.sharp};
  }

  &:focus {
    outline: none;

    svg {
      transform: scale(1.2);
    }
  }

  &:focus,
  &:hover {
    background: none;
  }
`;
const CloseIcon = styled(SvgSearchClose) `
  display: block;
  color: ${cssVar('fgColorAlt')};
  height: 33px;
  width: 33px;
`;
const CloseLabel = styled.span `
  ${srOnly}
`;
const ResultsContainer = styled(m.div) `
  position: absolute;
  top: 100%;
  left: 0;
  background: ${cssVar('bgColor')};
  border-radius: 0 0 5px 5px;
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.2);
  max-height: 410px;
  width: 100%;
  z-index: 3;
`;
const ResultItem = styled.a `
  ${fonts.matter.regular}
  position: relative;
  display: flex;
  align-items: center;
  color: ${cssVar('fgColor')};
  height: 45px;
  font-size: 18px;
  line-height: 23px;
  overflow: hidden;
  border-top: 1px solid ${brand.sage.sageDark};
  padding: 0 10px;
  text-decoration: none;
  text-indent: 4px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;

  &:focus,
  &:hover {
    color: ${cssVar('fgColor')};
    text-decoration: underline;
  }

  &:last-child::after {
    content: none;
  }
`;
const ResultItemText = styled.span `
  overflow: hidden;
  text-overflow: ellipsis;
  transform: translate3d(0, 0, 0.1px);
  transition: transform ease-out 0.15s;

  @media (prefers-reduced-motion: reduce)  {
    transition: none;
  }
`;
const TermListContainer = styled.div `
  display: block;
  padding: 14px 0;

  @media ${from(Device.Tablet)} {
    padding: 40px 0;
  }
`;
const TermListTitle = styled.h2 `
  ${HeadingStyles.H3Styles};
  color: ${brand.white};
  margin: 0 0 20px;
  text-align: center;

  @media ${from(Device.Tablet)} {
    margin: 0 0 40px;
  }
`;
const TermList = styled.ul `
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;

  @media ${from(Device.Tablet)} {
    gap: 20px;
  }
`;
const TermItem = styled.li `
  display: inline-block;
  flex: 0 1 auto;
`;
export default {
    Close,
    CloseIcon,
    CloseLabel,
    Container,
    Inner,
    Input,
    ResultItem,
    ResultItemText,
    ResultsContainer,
    SearchContainer,
    Submit,
    SubmitIcon,
    SubmitLabel,
    TermList,
    TermListContainer,
    TermListTitle,
    TermItem,
};
